import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { generateKeyFromLabel } from '../../pages/form-builder/fields/field-edit-base';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { LookupListService } from '../services/lookup-list.service';

/**
 * Formly field type for managing a list of {label, value} option pairs.
 *
 * Uses Formly's FieldArrayType as the foundation so the array of options
 * is managed entirely within the Formly reactive form model — no outside
 * component or manual array mutation needed.
 *
 * Each row is a Formly fieldGroup rendered by the `fieldArray` template
 * configured in `getOptionsEditorFieldConfig()` inside field-config-forms.ts.
 *
 * Features:
 * - Add rows via the "Add Option" button (calls FieldArrayType.add())
 * - Remove individual rows via the trash icon (calls FieldArrayType.remove(i))
 * - Duplicate label validation (warning shown inline, doesn't block save)
 * - Labels are user-generated content — no translation applied
 *
 * Usage in a FormlyFieldConfig:
 * ```typescript
 * {
 *   key: 'options',
 *   type: 'options-editor',
 *   fieldArray: {
 *     fieldGroup: [
 *       { key: 'label', type: 'text', props: { label: 'Label', placeholder: 'e.g. Yes', required: true } },
 *       { key: 'value', type: 'text', props: { label: 'Value', placeholder: 'e.g. yes', required: true } },
 *     ]
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-options-editor-formly-type',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    IconComponent,
    TranslatePipe,
  ],
  templateUrl: './options-editor-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './options-editor-formly-type.component.css',
})
export class OptionsEditorFormlyTypeComponent
  extends FieldArrayType
  implements AfterViewInit, OnDestroy
{
  private lookupListService = inject(LookupListService);

  // ─── Promote to Lookup List ───────────────────────────────────────────────

  isPromoting = signal(false);
  promoteName = signal('');
  isPromoteSaving = signal(false);

  // ─── Tab state ────────────────────────────────────────────────────────────

  /** Controls which property column is visible: the label or the value. */
  activeTab = signal<'labels' | 'values' | 'metrics'>('labels');

  switchTab(tab: 'labels' | 'values' | 'metrics'): void {
    this.activeTab.set(tab);
  }

  /** True when the field is currently configured for inline options (not a lookup list). */
  get showPromote(): boolean {
    if (this.isStateTransitionMode()) {
      return false;
    }
    return this.field.parent?.model?.['optionsSource'] !== 'lookup-list';
  }

  isStateTransitionMode(): boolean {
    const keys = this.getFieldArrayKeys();
    return keys.includes('from') && keys.includes('to') && !keys.includes('label');
  }

  getRowFieldByKey(rowField: any, key: string): any | null {
    const rowFields = rowField?.fieldGroup;
    if (!Array.isArray(rowFields)) {
      return null;
    }

    const match = rowFields.find((candidate: any) => candidate?.key === key);
    return match ?? null;
  }

  startPromote(): void {
    const label = this.field.parent?.model?.['label'] ?? '';
    this.promoteName.set(label);
    this.isPromoting.set(true);
  }

  cancelPromote(): void {
    this.isPromoting.set(false);
    this.promoteName.set('');
  }

  async confirmPromote(): Promise<void> {
    const name = this.promoteName().trim();
    if (!name) return;

    this.isPromoteSaving.set(true);
    try {
      // Get the current inline options from the form array
      const currentOptions = (this.formControl as FormArray).getRawValue() ?? [];

      const newList = await this.lookupListService.create({ name, options: currentOptions });

      // Update the parent form group to switch to lookup-list source
      const parentGroup = this.formControl.parent as FormGroup | null;
      parentGroup?.get('optionsSource')?.setValue('lookup-list');
      parentGroup?.get('lookupListId')?.setValue(newList.id);

      this.isPromoting.set(false);
      this.promoteName.set('');
    } catch (err) {
      // Leave isPromoting true so the user can retry
    } finally {
      this.isPromoteSaving.set(false);
    }
  }

  // ─── Label → Value auto-fill ─────────────────────────────────────────────

  /**
   * Tracks the last auto-generated value for each option's label control.
   * Key: the label AbstractControl reference (stable across moves/reorders).
   * Value: the last value produced by generateKeyFromLabel for that control.
   *
   * Auto-update logic: when the user types in the label field, the value field
   * is auto-updated using generateKeyFromLabel — but only while the value still
   * matches the previously auto-generated string (i.e. the user hasn't manually
   * overridden it).
   */
  private previousAutoValues = new Map<AbstractControl, string>();
  private optionSubscriptions: Subscription[] = [];

  ngAfterViewInit(): void {
    // Set up label→value subscriptions for any options already in the model
    const length = this.formControl?.length ?? 0;
    for (let i = 0; i < length; i++) {
      this.subscribeToLabelChange(i);
    }
  }

  override add(initialModel?: any, markAsDirty?: boolean): void {
    const modelToInsert = initialModel ?? this.buildDefaultRowModel();
    super.add(modelToInsert, markAsDirty);

    // Wait one tick for Formly to add the new FormGroup to the array
    setTimeout(() => {
      const lastIndex = (this.formControl?.length ?? 1) - 1;
      this.subscribeToLabelChange(lastIndex);
    });
  }

  private getFieldArrayKeys(): string[] {
    const rawFieldArray = this.field?.fieldArray;
    if (!rawFieldArray) {
      return [];
    }

    const fieldArray =
      typeof rawFieldArray === 'function'
        ? (rawFieldArray(this.field) as { fieldGroup?: Array<{ key?: unknown }> } | undefined)
        : rawFieldArray;

    const fieldGroup = fieldArray?.fieldGroup;
    if (!Array.isArray(fieldGroup)) {
      return [];
    }

    return fieldGroup
      .map((groupField) => (typeof groupField.key === 'string' ? groupField.key : ''))
      .filter((key) => key !== '');
  }

  private buildDefaultRowModel(): Record<string, unknown> | undefined {
    const rawFieldArray = this.field?.fieldArray;
    if (!rawFieldArray) {
      return undefined;
    }

    const fieldArray =
      typeof rawFieldArray === 'function'
        ? (rawFieldArray(this.field) as { fieldGroup?: Array<{ key?: unknown }> } | undefined)
        : rawFieldArray;

    if (!fieldArray) {
      return undefined;
    }

    const fieldGroup = fieldArray.fieldGroup;
    if (!Array.isArray(fieldGroup) || fieldGroup.length === 0) {
      return undefined;
    }

    const defaults: Record<string, unknown> = {};
    let hasAnyKey = false;

    for (const field of fieldGroup) {
      if (typeof field.key !== 'string' || field.key.trim() === '') {
        continue;
      }

      hasAnyKey = true;
      switch (field.key) {
        case 'from':
          defaults[field.key] = [];
          break;
        case 'isDefault':
          defaults[field.key] = false;
          break;
        case 'metricAmount':
          defaults[field.key] = null;
          break;
        default:
          defaults[field.key] = '';
          break;
      }
    }

    return hasAnyKey ? defaults : undefined;
  }

  ngOnDestroy(): void {
    this.optionSubscriptions.forEach((s) => s.unsubscribe());
    this.optionSubscriptions = [];
  }

  /**
   * Subscribe to value-changes on the label control at row `i` and mirror
   * the generated key into the value control, unless the user has manually
   * overridden the value.
   */
  private subscribeToLabelChange(i: number): void {
    const labelControl = this.formControl?.at(i)?.get('label');
    const valueControl = this.formControl?.at(i)?.get('value');
    if (!labelControl || !valueControl) return;

    // Avoid double-subscribing when called multiple times for the same control
    if (this.previousAutoValues.has(labelControl)) return;

    // Initialise: treat the current value as the "auto-generated baseline" so
    // the user can immediately start editing the label and see the value update.
    this.previousAutoValues.set(labelControl, generateKeyFromLabel(labelControl.value ?? ''));

    const sub = labelControl.valueChanges.subscribe((labelValue: string) => {
      const newAutoValue = generateKeyFromLabel(labelValue ?? '');
      const currentValue: string = valueControl.value ?? '';
      const prevAuto = this.previousAutoValues.get(labelControl) ?? '';

      // Only auto-update the value if it is blank or still matches the previous
      // auto-generated value (meaning the user has not manually overridden it).
      if (currentValue === '' || currentValue === prevAuto) {
        valueControl.setValue(newAutoValue, { emitEvent: false });
      }

      this.previousAutoValues.set(labelControl, newAutoValue);
    });

    this.optionSubscriptions.push(sub);
  }

  // ─── Reorder ──────────────────────────────────────────────────────────────

  /** Swap the values of two adjacent rows by index (a < b). */
  private swapRows(a: number, b: number): void {
    const fa = this.formControl.at(a) as FormGroup;
    const fb = this.formControl.at(b) as FormGroup;
    const valA = fa.getRawValue();
    const valB = fb.getRawValue();
    fb.patchValue(valA, { emitEvent: false });
    fa.patchValue(valB);
  }

  moveUp(i: number): void {
    if (i > 0) this.swapRows(i - 1, i);
  }

  moveDown(i: number): void {
    if (i < (this.formControl.length ?? 0) - 1) this.swapRows(i, i + 1);
  }

  // ─── Default option ───────────────────────────────────────────────────────

  /**
   * Returns true when the option at index `i` has isDefault === true in the
   * Formly-managed FormArray.  Falls back gracefully if the key is absent.
   */
  isDefaultAt(i: number): boolean {
    const defaultControl = (this.formControl as FormArray).at(i)?.get('isDefault');
    return !!defaultControl?.value;
  }

  /**
   * Toggle the default flag for option `i`.
   * Only one option can be the default at a time; clicking the already-starred
   * option clears it (no default).
   *
   * The `isDefault` control is never rendered via `<formly-field>` (the star
   * button drives it directly instead), so Formly's per-field model sync —
   * which only runs for fields actually instantiated in the template — never
   * fires for it. `setValue(..., { emitEvent: false })` on the reset loop
   * would leave that sync stale either way. So the row models are written
   * directly here rather than relying on Formly to propagate the control
   * values back into `this.model`.
   */
  toggleDefault(i: number): void {
    const arr = this.formControl as FormArray;
    if (!arr.at(i)?.get('isDefault')) {
      return;
    }

    const wasDefault = this.isDefaultAt(i);
    const rows = this.model as Array<{ isDefault?: boolean }> | undefined;

    for (let j = 0; j < (arr.length ?? 0); j++) {
      arr.at(j).get('isDefault')?.setValue(false, { emitEvent: false });
      if (rows?.[j]) {
        rows[j].isDefault = false;
      }
    }
    if (!wasDefault) {
      arr.at(i).get('isDefault')?.setValue(true, { emitEvent: false });
      if (rows?.[i]) {
        rows[i].isDefault = true;
      }
    }
  }

  // ─── Duplicate detection ─────────────────────────────────────────────────

  /**
   * Returns the label value for a given row index, used to check for duplicates.
   */
  private getLabelAt(i: number): string {
    return this.formControl?.at(i)?.get('label')?.value ?? '';
  }

  /**
   * Returns true if the label at index `i` is a duplicate of any other row's label.
   * Comparison is case-insensitive and trimmed.
   */
  isDuplicateLabel(i: number): boolean {
    const val = this.getLabelAt(i).trim().toLowerCase();
    if (!val) return false;
    const controls = this.formControl?.controls ?? [];
    return (
      controls.filter((_, idx) => {
        const other = this.formControl?.at(idx)?.get('label')?.value ?? '';
        return other.trim().toLowerCase() === val;
      }).length > 1
    );
  }
}
